<template>
  <div 
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <div class="p-6">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Commande {{ order.order_number }}</h3>
            <p class="text-sm text-gray-500">
              {{ formatDateFull(order.created_at) }}
            </p>
          </div>
          <button 
            @click="$emit('close')"
            class="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <icon name="X" :size="20" class="text-gray-500" />
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <!-- Informations client -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="User" :size="16" /> Informations client
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
              <p class="font-medium text-gray-900">{{ order.customer_name }}</p>
              <p class="text-gray-700">{{ order.customer_email || order.user?.email }}</p>
              <p class="text-gray-700 flex items-center gap-2">
                <icon name="Phone" :size="14" /> {{ order.customer_phone }}
              </p>
            </div>
          </div>

          <!-- Adresse de livraison -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="MapPin" :size="16" /> Adresse de livraison
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-line">
              {{ order.shipping_address }}
            </div>
          </div>

          <!-- Informations de commande -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="CreditCard" :size="16" /> Paiement & Livraison
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600">Mode de paiement:</span>
                <span class="font-medium">{{ formatPaymentMethod(order.payment_method) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Statut paiement:</span>
                <span :class="`px-2 py-0.5 rounded text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`">
                  {{ getPaymentStatusLabel(order.payment_status) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Frais de livraison:</span>
                <span class="font-medium">{{ formatPrice(order.shipping || 0) }}</span>
              </div>
            </div>
          </div>

          <!-- Statut de commande -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="Package" :size="16" /> Statut de commande
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
              <div class="flex items-center justify-between">
                <span class="text-gray-600">Statut:</span>
                <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`">
                  {{ order.status_text || getStatusLabel(order.status) }}
                </span>
              </div>
              
              <div v-if="order.notes" class="mt-4">
                <p class="text-gray-600 mb-1">Notes:</p>
                <p class="text-gray-700 italic">"{{ order.notes }}"</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Articles commandés -->
        <div class="space-y-4">
          <h4 class="font-medium text-gray-700 flex items-center gap-2">
            <icon name="Package" :size="16" /> Articles commandés
          </h4>
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left p-3 text-sm font-medium text-gray-500">Produit</th>
                  <th class="text-center p-3 text-sm font-medium text-gray-500">Qté</th>
                  <th class="text-center p-3 text-sm font-medium text-gray-500">Taille</th>
                  <th class="text-center p-3 text-sm font-medium text-gray-500">Couleur</th>
                  <th class="text-center p-3 text-sm font-medium text-gray-500">Fournisseur</th>
                  <th class="text-right p-3 text-sm font-medium text-gray-500">Prix unitaire</th>
                  <th class="text-right p-3 text-sm font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="(item, index) in orderItems" 
                  :key="index"
                  class="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td class="p-3 text-gray-700">
                    <div>
                      <p class="font-medium">{{ item.product_name }}</p>
                      <p v-if="item.product" class="text-xs text-gray-500">
                        Réf: {{ item.product.sku || item.product_id }}
                      </p>
                    </div>
                  </td>
                  <td class="p-3 text-center text-gray-700">{{ item.quantity }}</td>
                  <td class="p-3 text-center text-gray-700">{{ item.size || '—' }}</td>
                  <td class="p-3 text-center text-gray-700">
                    <span v-if="item.color" class="inline-flex items-center gap-1">
                      <span 
                        v-if="item.color.startsWith('#')"
                        class="w-3 h-3 rounded-full border border-gray-300"
                        :style="{ backgroundColor: item.color }"
                      ></span>
                      {{ item.color }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td class="p-3 text-center text-gray-700">
                    {{ item.supplier?.name || '—' }}
                  </td>
                  <td class="p-3 text-right text-gray-700">{{ formatPrice(item.price) }}</td>
                  <td class="p-3 text-right text-gray-700 font-medium">
                    {{ formatPrice(item.total) }}
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-100">
                <tr class="border-t border-gray-200">
                  <td colspan="5" class="p-3 text-right font-medium text-gray-700">Sous-total</td>
                  <td colspan="2" class="p-3 text-right font-medium text-gray-700">
                    {{ formatPrice(order.subtotal) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="5" class="p-3 text-right font-medium text-gray-700">Livraison</td>
                  <td colspan="2" class="p-3 text-right font-medium text-gray-700">
                    {{ formatPrice(order.shipping || 0) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="5" class="p-3 text-right font-medium text-gray-700">Remise</td>
                  <td colspan="2" class="p-3 text-right font-medium text-gray-700">
                    {{ formatPrice(order.discount || 0) }}
                  </td>
                </tr>
                <tr class="border-t border-gray-300">
                  <td colspan="5" class="p-3 text-right font-bold text-gray-900">Total</td>
                  <td colspan="2" class="p-3 text-right font-bold text-lg text-gray-900">
                    {{ formatPrice(order.total) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div class="flex items-center space-x-4">
            <span :class="`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`">
              {{ order.status_text || getStatusLabel(order.status) }}
            </span>
            <span :class="`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(order.payment_status)}`">
              {{ getPaymentStatusLabel(order.payment_status) }}
            </span>
          </div>
          <div class="flex items-center space-x-2">
            <button 
              @click="printInvoice"
              class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
            >
              <icon name="Printer" :size="16" /> Imprimer la facture
            </button>
            <button 
              @click="$emit('close')"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Template d'impression caché -->
  <div v-if="isPrinting" id="print-area" class="hidden">
    <div class="invoice-container">
      <!-- En-tête de la facture -->
      <div class="invoice-header">
        <div class="company-info">
          <h2>cassuss</h2>
          <p>Tél: +237694118310</p>
          <p>Email: contact@cassuss.com</p>
          <p>Adresse: Douala, Cameroun</p>
          
        </div>
        <div class="invoice-title">
          <h1>FACTURE</h1>
          <div class="invoice-meta">
            <p><strong>Numéro:</strong> {{ order.order_number }}</p>
            <p><strong>Date:</strong> {{ formatDate(order.created_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Informations client -->
      <div class="client-info">
        <h3>Client</h3>
        <p><strong>{{ order.customer_name }}</strong></p>
        <p>{{ order.customer_email || order.user?.email }}</p>
        <p>{{ order.customer_phone }}</p>
        <div class="address">
          <strong>Adresse de livraison:</strong><br>
          {{ order.shipping_address }}
        </div>
      </div>

      <!-- Détails de la commande -->
      <div class="order-details">
        <table class="invoice-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Référence</th>
              <th>Qté</th>
              <th>Taille</th>
              <th>Couleur</th>
              <th>Prix unitaire</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in orderItems" :key="index">
              <td>{{ item.product_name }}</td>
              <td class="text-center">{{ item.product?.sku || item.product_id }}</td>
              <td class="text-center">{{ item.quantity }}</td>
              <td class="text-center">{{ item.size || '—' }}</td>
              <td class="text-center">{{ item.color || '—' }}</td>
              <td class="text-right">{{ formatPrice(item.price) }}</td>
              <td class="text-right">{{ formatPrice(item.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totaux -->
      <div class="totals">
        <div class="totals-grid">
          <div class="total-row">
            <span>Sous-total:</span>
            <span>{{ formatPrice(order.subtotal) }}</span>
          </div>
          <div class="total-row">
            <span>Livraison:</span>
            <span>{{ formatPrice(order.shipping || 0) }}</span>
          </div>
          <div class="total-row" v-if="order.discount">
            <span>Remise:</span>
            <span>-{{ formatPrice(order.discount) }}</span>
          </div>
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>{{ formatPrice(order.total) }}</span>
          </div>
        </div>
      </div>

      <!-- Informations de paiement -->
      <div class="payment-info">
        <h3>Informations de paiement</h3>
        <p><strong>Mode de paiement:</strong> {{ formatPaymentMethod(order.payment_method) }}</p>
        <p><strong>Statut:</strong> {{ getPaymentStatusLabel(order.payment_status) }}</p>
        <p><strong>Statut commande:</strong> {{ getStatusLabel(order.status) }}</p>
        <p v-if="order.notes"><strong>Notes:</strong> {{ order.notes }}</p>
      </div>

      <!-- Pied de page -->
      <div class="footer">
        <div class="terms">
          <p><strong>Conditions de vente:</strong></p>
          <p>Les marchandises vendues ne sont ni reprises ni échangées.</p>
          <p>En cas de problème, contactez-nous dans les 7 jours.</p>
        </div>
        <div class="signature">
          <p>Signature et cachet</p>
          <div class="signature-line"></div>
        </div>
      </div>

      <!-- <div class="print-footer">
        Facture {{ order.order_number }} - Imprimée le {{ currentDate }}
      </div> -->
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderDetailModal',
  
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      isPrinting: false,
      currentDate: new Date().toLocaleDateString('fr-FR')
    }
  },
  
  computed: {
    orderItems() {
      return this.order.order_items || this.order.items || []
    }
  },
  
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF'
      }).format(price)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    formatDateFull(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getStatusColor(status) {
      switch (parseInt(status)) {
        case 4: return 'bg-green-100 text-green-800'
        case 3: return 'bg-blue-100 text-blue-800'
        case 2: return 'bg-purple-100 text-purple-800'
        case 1: return 'bg-yellow-100 text-yellow-800'
        case 0: return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        0: 'Rejeté',
        1: 'En cours',
        2: 'Accepté',
        3: 'Expédié',
        4: 'Livré'
      }
      return labels[statusNum] || `Statut ${status}`
    },
    
    getPaymentStatusColor(status) {
      switch (parseInt(status)) {
        case 2: return 'bg-green-100 text-green-800'
        case 1: return 'bg-yellow-100 text-yellow-800'
        case 3: return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getPaymentStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        1: 'En attente',
        2: 'Payé',
        3: 'Échoué'
      }
      return labels[statusNum] || `Paiement ${status}`
    },
    
    formatPaymentMethod(method) {
      const methods = {
        'card': 'Carte bancaire',
        'mobile_money': 'Mobile Money',
        'campay': 'Campay',
        'cash': 'Paiement à la livraison'
      }
      return methods[method] || method || 'Non spécifié'
    },
    
    printInvoice() {
      this.isPrinting = true
      
      this.$nextTick(() => {
        const printWindow = window.open('', '_blank')
        const printContent = document.getElementById('print-area').innerHTML
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Facture ${this.order.order_number}</title>
            <style>
              @media print {
                @page {
                  margin: 10mm;
                  size: A4 portrait;
                }
                
                body {
                  font-family: 'Helvetica', 'Arial', sans-serif;
                  color: #333;
                  margin: 0;
                  padding: 0;
                }
                
                .invoice-container {
                  max-width: 210mm;
                  margin: 0 auto;
                  padding: 20px;
                  background: white;
                }
                
                .invoice-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  border-bottom: 3px solid #2563eb;
                  padding-bottom: 20px;
                  margin-bottom: 20px;
                }
                
                .company-info h2 {
                  color: #2563eb;
                  margin: 0 0 5px 0;
                  font-size: 18px;
                }
                
                .company-info p {
                  margin: 2px 0;
                  font-size: 12px;
                  color: #666;
                }
                
                .invoice-title h1 {
                  color: #2563eb;
                  margin: 0;
                  font-size: 28px;
                  text-align: right;
                }
                
                .invoice-meta {
                  text-align: right;
                  margin-top: 10px;
                }
                
                .invoice-meta p {
                  margin: 2px 0;
                  font-size: 12px;
                }
                
                .client-info {
                  margin-bottom: 30px;
                  padding: 15px;
                  background: #f8fafc;
                  border-radius: 5px;
                }
                
                .client-info h3 {
                  margin-top: 0;
                  color: #2563eb;
                  border-bottom: 1px solid #e2e8f0;
                  padding-bottom: 5px;
                }
                
                .address {
                  margin-top: 10px;
                  padding: 10px;
                  background: white;
                  border: 1px solid #e2e8f0;
                  border-radius: 3px;
                }
                
                .order-details {
                  margin: 30px 0;
                }
                
                .invoice-table {
                  width: 100%;
                  border-collapse: collapse;
                  font-size: 12px;
                }
                
                .invoice-table th {
                  background: #2563eb;
                  color: white;
                  padding: 10px;
                  text-align: left;
                  font-weight: bold;
                }
                
                .invoice-table td {
                  padding: 10px;
                  border-bottom: 1px solid #e2e8f0;
                  vertical-align: top;
                }
                
                .invoice-table tbody tr:nth-child(even) {
                  background: #f8fafc;
                }
                
                .totals {
                  margin-top: 30px;
                  text-align: right;
                }
                
                .totals-grid {
                  display: inline-block;
                  min-width: 300px;
                }
                
                .total-row {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 10px;
                  padding: 8px 0;
                  border-bottom: 1px dashed #e2e8f0;
                }
                
                .grand-total {
                  font-size: 16px;
                  font-weight: bold;
                  color: #2563eb;
                  border-top: 2px solid #2563eb;
                  border-bottom: none;
                  margin-top: 10px;
                  padding-top: 15px;
                }
                
                .payment-info {
                  margin-top: 30px;
                  padding: 20px;
                  background: #f8fafc;
                  border-radius: 5px;
                }
                
                .payment-info h3 {
                  margin-top: 0;
                  color: #2563eb;
                }
                
                .footer {
                  display: flex;
                  justify-content: space-between;
                  margin-top: 40px;
                  padding-top: 20px;
                  border-top: 2px solid #e2e8f0;
                }
                
                .terms {
                  font-size: 11px;
                  color: #666;
                  max-width: 60%;
                }
                
                .signature {
                  text-align: center;
                }
                
                .signature-line {
                  width: 200px;
                  height: 1px;
                  background: #333;
                  margin-top: 40px;
                }
                
                .print-footer {
                  margin-top: 40px;
                  text-align: center;
                  font-size: 10px;
                  color: #999;
                  border-top: 1px solid #e2e8f0;
                  padding-top: 10px;
                }
              }
              
              /* Styles pour l'aperçu avant impression */
              @media screen {
                #print-area {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `)
        
        printWindow.document.close()
        printWindow.focus()
        
        setTimeout(() => {
          printWindow.print()
          printWindow.onafterprint = () => {
            printWindow.close()
            this.isPrinting = false
          }
        }, 500)
      })
    }
  }
}
</script>

<style scoped>
/* Styles spécifiques au composant */
.hidden {
  display: none;
}

/* Styles supplémentaires pour la facture en ligne */
@media screen {
  .invoice-container {
    max-width: 800px;
    margin: 0 auto;
  }
}

/* Animation pour l'impression */
@keyframes print-loading {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive pour les petits écrans */
@media (max-width: 768px) {
  .invoice-header {
    flex-direction: column;
    text-align: center;
  }
  
  .invoice-title {
    margin-top: 20px;
  }
  
  .footer {
    flex-direction: column;
  }
  
  .terms {
    max-width: 100% !important;
    margin-bottom: 20px;
  }
}
</style>
